import type { Theme } from '../types';
import { RELATIONSHIP_CONFIG, daysSince } from '../relationshipConfig';

interface MilestonesProps {
  theme: Theme;
}

interface Milestone {
  key: string;
  label: string;
  emoji: string;
  date: Date;
  daysAgo: number;
}

const Milestones = ({ theme }: MilestonesProps) => {
  // Build milestones array from config
  const milestones: Milestone[] = Object.entries(RELATIONSHIP_CONFIG.milestones)
    .map(([key, date]) => ({
      key,
      label: formatMilestoneLabel(key),
      emoji: getMilestoneEmoji(key),
      date,
      daysAgo: daysSince(date)
    }))
    .sort((a, b) => b.date.getTime() - a.date.getTime()); // Most recent first

  function formatMilestoneLabel(key: string): string {
    // Convert camelCase to Title Case
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  function getMilestoneEmoji(key: string): string {
    const emojiMap: Record<string, string> = {
      firstMet: '👋',
      firstDate: '🌹',
      firstKiss: '💋',
      officiallyTogether: '💑',
      firstTrip: '✈️',
      movedInTogether: '🏠',
      engagement: '💍',
      wedding: '💒',
      anniversary: '🎉'
    };
    
    return emojiMap[key] || '⭐';
  }

  function formatDate(date: Date): string {
    return date.toLocaleDateString(undefined, { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  }

  function getDaysAgoText(days: number): string {
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    
    const years = Math.floor(days / 365);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }

  if (milestones.length === 0) return null;

  return (
    <div className={`${theme.secondary} rounded-2xl p-6 shadow-lg`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-xl font-bold ${theme.text} flex items-center gap-2`}>
          <span>📖</span>
          Our Story
        </h3>
      </div>

      {/* Timeline */}
      <div className="space-y-3">
        {milestones.map((milestone, _) => (
          <div
            key={milestone.key}
            className="bg-white rounded-lg p-4 shadow hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              {/* Emoji */}
              <div className="text-3xl">{milestone.emoji}</div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className={`font-semibold ${theme.text}`}>
                    {milestone.label}
                  </h4>
                  <span className={`text-xs ${theme.accent} font-medium`}>
                    {getDaysAgoText(milestone.daysAgo)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {formatDate(milestone.date)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {milestone.daysAgo.toLocaleString()} days ago
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Milestones;